import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export interface AlertOptions {
    heading?: string;
    keepAfterRouteChange?: boolean;
    dismissible?: boolean;
    autoClose?: boolean;
}

export class Alert {
    type: string = '';
    message: string = '';
    heading?: string = '';
    clearPriorAlerts?: boolean = true;
    keepAfterRouteChange?: boolean = false;
    hasKeptAfterRouteChange?: boolean = false;
    dismissible?: boolean = false;
    autoClose?: boolean = false;

    constructor(init?: Partial<Alert>) {
        Object.assign(this, init);
    }
}

export const AlertType = {
    success: 'success',
    danger: 'danger',
    info: 'info',
    warning: 'warning',
    primary: 'primary',
};

/*
 ** The message service enables any component in the application to display alert messages at the top of the page via the app component.
 ** It has methods for displaying success and error messages, and a getMessage() method that returns an Observable that is used by the app
 ** component to subscribe to notifications for whenever a message should be displayed.
 */

@Injectable({ providedIn: 'root' })
export class AlertService {
    private alertSubject = new Subject<any>();

    constructor() {}

    // Alert Component should subscribe to alerts for display
    onAlert(): Observable<Alert> {
        return this.alertSubject.asObservable(); //.pipe(filter((x) => x && x.id === id))
    }

    // Add a new alert with a passed alert object
    addAlert(alert: Alert) {
        this.alert(alert);
    }

    // Convenience methods for each type of alert. Type is auto set and message is passed in with options like heading and keepAfterRouteChange
    success(message: string, options?: AlertOptions) {
        this.alert(new Alert({ ...options, type: AlertType.success, message }));
    }

    error(message: string, options?: AlertOptions) {
        this.alert(new Alert({ ...options, type: AlertType.danger, message }));
    }

    info(message: string, options?: AlertOptions) {
        this.alert(new Alert({ ...options, type: AlertType.info, message }));
    }

    primary(message: string, options?: AlertOptions) {
        this.alert(new Alert({ ...options, type: AlertType.primary, message }));
    }

    warn(message: string, options?: AlertOptions) {
        this.alert(new Alert({ ...options, type: AlertType.warning, message }));
    }

    // Clear alerts
    clearAlerts() {
        this.alertSubject.next(undefined);
    }

    // Capture alert ID and Routing option and add to BehaviorSubject
    // The corresponding alert component will subscribe to the BehaviorSubject and display the alert
    private alert(alert: Alert) {
        this.alertSubject.next(alert);
    }

    ngOnDestroy() {
        this.alertSubject.unsubscribe();
    }
}
